// Service for Pip's Word Puzzle dictionary validation using Supabase
import { supabase } from '../../../services/supabase';
import { isWordInDictionary as isWordInLocalDictionary } from './wordlist';
import { logger } from '../../../utils/logger';

// Cache for validated words to minimize database queries
const validatedWordsCache = new Map<string, boolean>();
const CACHE_MAX_SIZE = 1000;

// Flag to track if we should use database or fall back to local
let useDatabaseValidation = true;
let databaseCheckAttempted = false;

// localStorage keys
const LOCAL_STORAGE_DICTIONARY_KEY = 'nerdle_dictionary';
const LOCAL_STORAGE_DICTIONARY_VERSION_KEY = 'nerdle_dictionary_version';
const CURRENT_DICTIONARY_VERSION = '1.0';

// In-memory set of all valid words (loaded from localStorage)
let localDictionarySet: Set<string> | null = null;

/**
 * Initialize the local dictionary from localStorage or download from database
 * Call this on app start for best performance
 */
export async function initializeDictionary(): Promise<void> {
  // Try to load from localStorage first
  try {
    const storedVersion = localStorage.getItem(LOCAL_STORAGE_DICTIONARY_VERSION_KEY);
    const storedDictionary = localStorage.getItem(LOCAL_STORAGE_DICTIONARY_KEY);

    if (storedVersion === CURRENT_DICTIONARY_VERSION && storedDictionary) {
      const words = JSON.parse(storedDictionary);
      localDictionarySet = new Set(words);
      logger.log(`✅ Loaded ${localDictionarySet.size} words from localStorage`);
      return;
    }
  } catch (error) {
    logger.warn('Failed to load dictionary from localStorage:', error);
  }

  // Download fresh dictionary from database
  await downloadDictionary();
}

/**
 * Download all words from database and store in localStorage
 */
async function downloadDictionary(): Promise<void> {
  try {
    logger.log('Downloading dictionary from database...');

    const { data, error } = await supabase
      .from('nerdle_words')
      .select('word');

    if (error) throw error;

    if (data && data.length > 0) {
      const words = data.map(row => row.word.toUpperCase());
      localDictionarySet = new Set(words);

      // Save to localStorage
      localStorage.setItem(LOCAL_STORAGE_DICTIONARY_KEY, JSON.stringify(words));
      localStorage.setItem(LOCAL_STORAGE_DICTIONARY_VERSION_KEY, CURRENT_DICTIONARY_VERSION);

      logger.log(`✅ Downloaded and cached ${words.length} words from database`);
    } else {
      logger.warn('No words found in database, using built-in wordlist');
      localDictionarySet = new Set();
    }
  } catch (error) {
    logger.error('Failed to download dictionary:', error);
    // Use empty set, will fall back to built-in wordlist
    localDictionarySet = new Set();
  }
}

/**
 * Check if a word is valid using LOCAL-FIRST approach
 * 1. Check localStorage dictionary (fast, works offline)
 * 2. Fall back to built-in wordlist
 * 3. Optionally verify with database (anti-cheat)
 */
export async function isWordValid(word: string): Promise<boolean> {
  const wordUpper = word.toUpperCase();

  // Check memory cache first (fastest)
  if (validatedWordsCache.has(wordUpper)) {
    return validatedWordsCache.get(wordUpper)!;
  }

  // Initialize dictionary if not loaded yet
  if (localDictionarySet === null) {
    await initializeDictionary();
  }

  // Check localStorage dictionary (primary source)
  if (localDictionarySet && localDictionarySet.size > 0) {
    const isValid = localDictionarySet.has(wordUpper);
    cacheWord(wordUpper, isValid);

    // If invalid in localStorage, also check built-in wordlist as fallback
    if (!isValid) {
      const isValidInBuiltIn = isWordInLocalDictionary(wordUpper);
      if (isValidInBuiltIn) {
        cacheWord(wordUpper, true);
        return true;
      }
    }

    return isValid;
  }

  // Final fallback to built-in wordlist (if localStorage failed)
  const isValid = isWordInLocalDictionary(wordUpper);
  cacheWord(wordUpper, isValid);
  return isValid;
}

/**
 * Check word against Supabase database
 */
async function checkWordInDatabase(word: string): Promise<boolean> {
  databaseCheckAttempted = true;

  const { data, error } = await supabase
    .from('nerdle_words')
    .select('word')
    .eq('word', word)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data !== null;
}

/**
 * Preload dictionary for optimal performance
 * Now uses the new local-first approach via initializeDictionary
 * @param currentPuzzleWord Optional current puzzle word to ensure it's cached immediately
 */
export async function preloadCommonWords(currentPuzzleWord?: string): Promise<void> {
  // Initialize the full dictionary (local-first)
  await initializeDictionary();

  // Always cache the current puzzle word immediately
  if (currentPuzzleWord) {
    const wordUpper = currentPuzzleWord.toUpperCase();
    cacheWord(wordUpper, true);
  }

  // Also fetch puzzle words to ensure they're in the dictionary
  try {
    const { data: puzzleWords, error: puzzleError } = await supabase
      .from('nerdle_puzzles')
      .select('word');

    if (!puzzleError && puzzleWords && puzzleWords.length > 0) {
      puzzleWords.forEach(row => {
        const wordUpper = row.word.toUpperCase();
        cacheWord(wordUpper, true);
        // Add to local dictionary set if not already there
        if (localDictionarySet) {
          localDictionarySet.add(wordUpper);
        }
      });
    }
  } catch (error) {
    logger.warn('Error preloading puzzle words:', error);
  }
}

/**
 * Add word to cache
 */
function cacheWord(word: string, isValid: boolean): void {
  // Implement simple LRU by clearing cache when it gets too large
  if (validatedWordsCache.size >= CACHE_MAX_SIZE) {
    // Clear oldest half of cache
    const keysToDelete = Array.from(validatedWordsCache.keys()).slice(0, CACHE_MAX_SIZE / 2);
    keysToDelete.forEach(key => validatedWordsCache.delete(key));
  }

  validatedWordsCache.set(word, isValid);
}

/**
 * Clear the word cache
 * Useful when you want to force fresh lookups
 */
export function clearCache(): void {
  validatedWordsCache.clear();
  logger.log('Dictionary cache cleared');
}

/**
 * Get cache statistics
 */
export function getCacheStats(): { size: number; usingDatabase: boolean } {
  return {
    size: validatedWordsCache.size,
    usingDatabase: useDatabaseValidation && databaseCheckAttempted
  };
}

/**
 * Force enable or disable database validation
 */
export function setDatabaseValidation(enabled: boolean): void {
  useDatabaseValidation = enabled;
  logger.log(`Database validation ${enabled ? 'enabled' : 'disabled'}`);
}
